import React, { useCallback, useEffect, useState } from 'react';
import {
  CreatorChatActionRow,
  CreatorChatVideoJobRow,
  LumaModifyMode,
  VideoGenKind,
  VideoGenHealthStatus,
  VideoGenProvider,
  creatorChatAdminService,
} from '../../services/creatorChatService';

const ACTION_TEMPLATES = [
  { slug: 'kiss' as const, label: 'Blow a kiss', synonyms: 'blow kiss,kiss me,muah,blow me a kiss' },
  { slug: 'wave' as const, label: 'Wave', synonyms: 'wave,hi,hello,wave at me' },
  { slug: 'wink' as const, label: 'Wink', synonyms: 'wink,wink at me' },
  { slug: 'dance' as const, label: 'Dance', synonyms: 'dance,dance for me,move for me' },
];

const LUMA_MODES: { value: LumaModifyMode; label: string }[] = [
  { value: 'adhere_1', label: 'Adhere 1 (subtle)' },
  { value: 'adhere_2', label: 'Adhere 2' },
  { value: 'adhere_3', label: 'Adhere 3' },
  { value: 'flex_1', label: 'Flex 1 (balanced)' },
  { value: 'flex_2', label: 'Flex 2' },
  { value: 'flex_3', label: 'Flex 3' },
  { value: 'reimagine_1', label: 'Reimagine 1' },
  { value: 'reimagine_2', label: 'Reimagine 2' },
  { value: 'reimagine_3', label: 'Reimagine 3 (strong)' },
];

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '8px 10px',
  border: '1px solid #e2e8f0',
  borderRadius: 8,
  fontSize: 13,
  boxSizing: 'border-box',
};

function jobStatusChip(status: string): { label: string; color: string; bg: string } {
  switch (status) {
    case 'PENDING':
    case 'RUNNING':
      return { label: 'Generating…', color: '#0369a1', bg: '#e0f2fe' };
    case 'SUCCEEDED':
      return { label: 'Ready', color: '#0d9488', bg: '#ccfbf1' };
    case 'FAILED':
      return { label: 'Failed', color: '#b91c1c', bg: '#fee2e2' };
    default:
      return { label: status, color: '#64748b', bg: '#f1f5f9' };
  }
}

function latestJobForKind(jobs: CreatorChatVideoJobRow[], kind: string): CreatorChatVideoJobRow | undefined {
  return jobs.find((j) => (j.actionSlug || 'idle') === kind);
}

type Props = {
  modalId: number;
  canManage: boolean;
  uploadsAllowed: boolean;
  avatarImageUrl: string;
  actionsEnabled: boolean;
  idleVideoUrl: string;
  onActionsEnabledChange: (v: boolean) => void;
  onIdleVideoUrlChange: (url: string) => void;
};

const AdminCreatorChatActionsPanel: React.FC<Props> = ({
  modalId,
  canManage,
  uploadsAllowed,
  avatarImageUrl,
  actionsEnabled,
  idleVideoUrl,
  onActionsEnabledChange,
  onIdleVideoUrlChange,
}) => {
  const [actions, setActions] = useState<CreatorChatActionRow[]>([]);
  const [jobs, setJobs] = useState<CreatorChatVideoJobRow[]>([]);
  const [videoGenHealth, setVideoGenHealth] = useState<VideoGenHealthStatus | null>(null);
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [uploadingId, setUploadingId] = useState<string | null>(null);
  const [wheelUpdatingId, setWheelUpdatingId] = useState<string | null>(null);
  const [generatingKind, setGeneratingKind] = useState<string | null>(null);
  const [customRequest, setCustomRequest] = useState('');
  const [genProvider, setGenProvider] = useState<VideoGenProvider>('fal');
  const [falModel, setFalModel] = useState('fal-ai/wan-i2v');
  const [stageProvider, setStageProvider] = useState<VideoGenProvider>('fal');
  const [stage1Prompt, setStage1Prompt] = useState('');
  const [stage2Prompt, setStage2Prompt] = useState('');
  const [stage3Prompt, setStage3Prompt] = useState('');
  const [stage2ImageUrl, setStage2ImageUrl] = useState('');
  const [stage3ImageUrl, setStage3ImageUrl] = useState('');
  const [generatingStage, setGeneratingStage] = useState<1 | 2 | 3 | null>(null);
  const [extractingFrameJobId, setExtractingFrameJobId] = useState<number | null>(null);
  const stage2FrameRef = React.useRef<HTMLInputElement>(null);
  const stage3FrameRef = React.useRef<HTMLInputElement>(null);
  const [lumaOpenJobId, setLumaOpenJobId] = useState<number | null>(null);
  const [lumaPrompt, setLumaPrompt] = useState('');
  const [lumaMode, setLumaMode] = useState<LumaModifyMode>('flex_1');
  const [lumaTriggers, setLumaTriggers] = useState('');
  const [modifyingJobId, setModifyingJobId] = useState<number | null>(null);

  // NEW: allow uploading a custom MP4/WebM and mapping trigger phrases to it.
  const [customVideoLabel, setCustomVideoLabel] = useState('');
  const [customVideoTriggers, setCustomVideoTriggers] = useState('');
  const [customVideoShowOnWheel, setCustomVideoShowOnWheel] = useState(true);
  const customVideoFileRef = React.useRef<HTMLInputElement>(null);

  const [pendingTemplate, setPendingTemplate] = useState<(typeof ACTION_TEMPLATES)[0] | null>(null);
  const templateFileRef = React.useRef<HTMLInputElement>(null);

  const hasAvatar = !!avatarImageUrl?.trim();
  const videoGenReady = !!videoGenHealth?.ok;
  const lumaReady = !!(videoGenHealth?.lumaOk ?? videoGenHealth?.luma?.ok);
  const sogniReady = !!(videoGenHealth?.sogniOk ?? videoGenHealth?.sogni?.ok);
  const selectedGenReady = genProvider === 'sogni' ? sogniReady : videoGenReady;
  const canGenerate = canManage && uploadsAllowed && hasAvatar && selectedGenReady;
  const canLumaModify = canManage && uploadsAllowed && lumaReady;
  const providerLabel = genProvider === 'sogni' ? 'Sogni' : 'fal.ai';
  const falModelOptions =
    videoGenHealth?.models && videoGenHealth.models.length > 0
      ? videoGenHealth.models
      : [
          { id: 'fal-ai/wan-i2v', label: 'Wan 2.1 i2v (default, open-source)' },
          { id: 'fal-ai/wan/v2.6/image-to-video/flash', label: 'Wan 2.6 Flash (cheaper)' },
          { id: 'fal-ai/wan/v2.6/image-to-video', label: 'Wan 2.6 i2v' },
          { id: 'fal-ai/wan/v2.7/image-to-video', label: 'Wan 2.7 i2v' },
          { id: 'fal-ai/kling-video/v2.1/standard/image-to-video', label: 'Kling 2.1 Standard' },
          { id: 'fal-ai/kling-video/v2.1/pro/image-to-video', label: 'Kling 2.1 Pro' },
        ];
  const healthInitRef = React.useRef(false);

  // Set default provider once when health first loads — do NOT keep forcing Sogni/fal after the user picks.
  useEffect(() => {
    if (healthInitRef.current || !videoGenHealth) return;
    healthInitRef.current = true;
    const defaultFal =
      videoGenHealth.defaultModel || videoGenHealth.model || 'fal-ai/wan-i2v';
    setFalModel(defaultFal);
    if (videoGenReady) {
      setGenProvider('fal');
      setStageProvider('fal');
    } else if (sogniReady) {
      setGenProvider('sogni');
      setStageProvider('sogni');
    }
  }, [videoGenHealth, videoGenReady, sogniReady]);
  // Keep parent callbacks/values in refs so load/poll don't re-bind every parent render
  // (inline onIdleVideoUrlChange was causing an infinite load ↔ setForm loop / flicker).
  const onIdleVideoUrlChangeRef = React.useRef(onIdleVideoUrlChange);
  const idleVideoUrlRef = React.useRef(idleVideoUrl);
  const jobsStatusKeyRef = React.useRef('');
  /** Bumped on delete so in-flight listVideoJobs polls cannot resurrect removed rows. */
  const jobsFetchGenRef = React.useRef(0);
  useEffect(() => {
    onIdleVideoUrlChangeRef.current = onIdleVideoUrlChange;
  }, [onIdleVideoUrlChange]);
  useEffect(() => {
    idleVideoUrlRef.current = idleVideoUrl;
  }, [idleVideoUrl]);

  const applyIdleUrlIfChanged = useCallback((url: string | null | undefined) => {
    if (!url || url === idleVideoUrlRef.current) return;
    idleVideoUrlRef.current = url;
    onIdleVideoUrlChangeRef.current(url);
  }, []);

  const load = useCallback(async (opts?: { silent?: boolean }) => {
    const silent = !!opts?.silent;
    if (!silent) setLoading(true);
    const fetchGen = ++jobsFetchGenRef.current;
    try {
      const [list, jobList] = await Promise.all([
        creatorChatAdminService.listActions(modalId),
        creatorChatAdminService.listVideoJobs(modalId),
      ]);
      if (fetchGen !== jobsFetchGenRef.current) return;
      setActions(list);
      setJobs(jobList);
      jobsStatusKeyRef.current = jobList.map((j) => `${j.id}:${j.status}`).join('|');

      const idleJob = latestJobForKind(jobList, 'idle');
      if (idleJob?.status === 'SUCCEEDED' && idleJob.resultVideoUrl) {
        applyIdleUrlIfChanged(idleJob.resultVideoUrl);
      }
    } catch {
      if (!silent) setMsg('Failed to load actions');
    } finally {
      if (!silent && fetchGen === jobsFetchGenRef.current) setLoading(false);
    }
  }, [modalId, applyIdleUrlIfChanged]);

  const loadVideoGenHealth = useCallback(async () => {
    try {
      const h = await creatorChatAdminService.videoGenHealth();
      setVideoGenHealth(h);
    } catch {
      setVideoGenHealth(null);
    }
  }, []);

  useEffect(() => {
    void load();
    void loadVideoGenHealth();
    // Only re-fetch when the selected modal changes — not when parent re-renders.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modalId]);

  // Always poll recent generations while this panel is open so Test persona / realtime
  // jobs appear without a full page refresh (previously we only polled when local state
  // already had PENDING/RUNNING, so brand-new jobs were invisible until reload).
  useEffect(() => {
    const refreshJobs = async () => {
      try {
        const fetchGen = jobsFetchGenRef.current;
        const jobList = await creatorChatAdminService.listVideoJobs(modalId);
        if (fetchGen !== jobsFetchGenRef.current) return;
        const nextKey = jobList.map((j) => `${j.id}:${j.status}`).join('|');
        const prevKey = jobsStatusKeyRef.current;
        setJobs(jobList);

        const idleJob = latestJobForKind(jobList, 'idle');
        if (idleJob?.status === 'SUCCEEDED' && idleJob.resultVideoUrl) {
          applyIdleUrlIfChanged(idleJob.resultVideoUrl);
        }

        if (nextKey !== prevKey) {
          jobsStatusKeyRef.current = nextKey;
          const newlyReady = jobList.some(
            (j) => j.status === 'SUCCEEDED' && !prevKey.includes(`${j.id}:SUCCEEDED`)
          );
          const newJobAppeared = jobList.some((j) => !prevKey.includes(`${j.id}:`));
          if (newlyReady || newJobAppeared || !prevKey) {
            await load({ silent: true });
          }
        }
      } catch {
        /* ignore background poll errors */
      }
    };

    const onJobsChanged = () => {
      void refreshJobs();
    };
    window.addEventListener('creator-chat-jobs-changed', onJobsChanged);

    const id = window.setInterval(() => {
      void refreshJobs();
    }, 5000);

    return () => {
      window.clearInterval(id);
      window.removeEventListener('creator-chat-jobs-changed', onJobsChanged);
    };
  }, [modalId, load, applyIdleUrlIfChanged]);

  const needsJobPolling = jobs.some(
    (j) =>
      j.status === 'PENDING' ||
      j.status === 'RUNNING' ||
      (j.status === 'FAILED' && !!j.providerRequestId && !j.resultVideoUrl)
  );

  const generateFromAvatar = async (kind: VideoGenKind, label: string, requestText?: string) => {
    if (!canGenerate) {
      if (!hasAvatar) setMsg('Upload an avatar image first (avatar section above).');
      else if (!uploadsAllowed) setMsg('Confirm content upload rights before generating video.');
      else if (genProvider === 'sogni' && !sogniReady) {
        setMsg(videoGenHealth?.sogni?.message || 'Sogni is not configured (set creator.chat.sogni-video.api-key).');
      } else if (!videoGenReady) setMsg('fal.ai video generation is not configured on the server.');
      return;
    }
    if (kind === 'custom') {
      const text = (requestText || customRequest || '').trim();
      if (!text) {
        setMsg('Type what you want her to do in the custom request box.');
        return;
      }
    }
    setGeneratingKind(kind === 'custom' ? 'custom' : kind);
    setMsg(null);
    try {
      await creatorChatAdminService.generateVideo(
        modalId,
        kind,
        kind === 'custom' ? (requestText || customRequest).trim() : undefined,
        genProvider,
        undefined,
        genProvider === 'fal' ? falModel : undefined
      );
      const jobList = await creatorChatAdminService.listVideoJobs(modalId);
      setJobs(jobList);
      const via =
        genProvider === 'sogni'
          ? 'Sogni'
          : `fal.ai (${falModelOptions.find((m) => m.id === falModel)?.label || falModel})`;
      setMsg(`Generating "${label}" via ${via} — this usually takes 30–120 seconds.`);
      if (kind === 'custom') {
        setCustomRequest('');
      }
    } catch (e: any) {
      setMsg(`Generate failed (${e?.message || 'error'})`);
    } finally {
      setGeneratingKind(null);
    }
  };

  const canGenerateStages =
    canManage &&
    uploadsAllowed &&
    !!avatarImageUrl.trim() &&
    (stageProvider === 'sogni' ? sogniReady : videoGenReady);

  const generateStage = async (stage: 1 | 2 | 3) => {
    if (!canGenerateStages) {
      if (stageProvider === 'sogni' && !sogniReady) {
        setMsg(videoGenHealth?.sogni?.message || 'Sogni is not configured.');
      } else if (stageProvider === 'fal' && !videoGenReady) {
        setMsg(videoGenHealth?.message || 'fal.ai is not configured.');
      } else if (!avatarImageUrl.trim()) setMsg('Upload an avatar image first.');
      else if (!uploadsAllowed) setMsg('Confirm content upload rights first.');
      return;
    }
    const prompt =
      stage === 1 ? stage1Prompt.trim() : stage === 2 ? stage2Prompt.trim() : stage3Prompt.trim();
    if (!prompt) {
      setMsg(`Enter a Stage ${stage} prompt first.`);
      return;
    }
    if (prompt.length > 500) {
      setMsg('Stage prompt must be 500 characters or less.');
      return;
    }
    let sourceImageUrl: string | undefined;
    if (stage === 1) {
      sourceImageUrl = avatarImageUrl.trim();
    } else if (stage === 2) {
      sourceImageUrl = stage2ImageUrl.trim();
      if (!sourceImageUrl) {
        setMsg('Stage 2 needs a start image — use last frame from Stage 1 (or paste a URL).');
        return;
      }
    } else {
      sourceImageUrl = stage3ImageUrl.trim();
      if (!sourceImageUrl) {
        setMsg('Stage 3 needs a start image — use last frame from Stage 2 (or paste a URL).');
        return;
      }
    }

    const viaLabel =
      stageProvider === 'sogni'
        ? 'Sogni'
        : `fal.ai (${falModelOptions.find((m) => m.id === falModel)?.label || falModel})`;

    setGeneratingStage(stage);
    setMsg(null);
    try {
      await creatorChatAdminService.generateVideo(
        modalId,
        'custom',
        prompt,
        stageProvider,
        sourceImageUrl,
        stageProvider === 'fal' ? falModel : undefined
      );
      const jobList = await creatorChatAdminService.listVideoJobs(modalId);
      setJobs(jobList);
      setMsg(
        `${viaLabel} Stage ${stage} started. When Ready: Download last frame / Use last frame → Stage ${
          stage < 3 ? stage + 1 : 'done — stitch clips in an editor if you want one file'
        }.`
      );
    } catch (e: any) {
      setMsg(`${viaLabel} Stage ${stage} failed (${e?.message || 'error'})`);
    } finally {
      setGeneratingStage(null);
    }
  };

  const uploadStageFrame = async (stage: 2 | 3, file: File) => {
    if (!uploadsAllowed) {
      setMsg('Confirm upload rights before uploading a stage frame.');
      return;
    }
    if (!file.type.startsWith('image/')) {
      setMsg('Stage start must be an image (jpg/png/webp) — export the last frame of the previous clip.');
      return;
    }
    setUploadingId(`stage${stage}-frame`);
    setMsg(null);
    try {
      const url = await creatorChatAdminService.uploadAvatar(file);
      if (stage === 2) setStage2ImageUrl(url);
      else setStage3ImageUrl(url);
      setMsg(`Stage ${stage} start image uploaded. You can Generate Stage ${stage} now.`);
    } catch (e: any) {
      setMsg(`Frame upload failed (${e?.message || 'error'})`);
    } finally {
      setUploadingId(null);
    }
  };

  const applyLastFrameForStage = async (job: CreatorChatVideoJobRow, targetStage: 2 | 3) => {
    if (!canManage || !job.resultVideoUrl) return;
    setExtractingFrameJobId(job.id);
    setMsg(null);
    try {
      const { url } = await creatorChatAdminService.extractLastFrame(modalId, { jobId: job.id });
      if (targetStage === 2) setStage2ImageUrl(url);
      else setStage3ImageUrl(url);
      setMsg(
        `Last frame from job #${job.id} set as Stage ${targetStage} start image. Scroll up to multi-stage, or use Download last frame to save the JPEG.`
      );
    } catch (e: any) {
      setMsg(`Extract last frame failed (${e?.message || 'error'}). Is FFmpeg installed on the server?`);
    } finally {
      setExtractingFrameJobId(null);
    }
  };

  const downloadImageFromUrl = async (url: string, filename: string) => {
    const safeName = filename.replace(/[^\w.\-]+/g, '_');
    try {
      const res = await fetch(url, { mode: 'cors', credentials: 'omit' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = objectUrl;
      a.download = safeName.endsWith('.jpg') || safeName.endsWith('.jpeg') || safeName.endsWith('.png')
        ? safeName
        : `${safeName}.jpg`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(objectUrl);
      return true;
    } catch {
      // Cross-origin without CORS: open image so user can Save As
      window.open(url, '_blank', 'noopener,noreferrer');
      return false;
    }
  };

  const downloadLastFrame = async (job: CreatorChatVideoJobRow) => {
    if (!canManage || !job.resultVideoUrl) return;
    setExtractingFrameJobId(job.id);
    setMsg(null);
    try {
      const { url } = await creatorChatAdminService.extractLastFrame(modalId, { jobId: job.id });
      const saved = await downloadImageFromUrl(url, `creator-chat-job-${job.id}-last-frame.jpg`);
      setMsg(
        saved
          ? `Downloaded last frame from job #${job.id}.`
          : `Opened last frame from job #${job.id} in a new tab — right-click → Save image as…`
      );
    } catch (e: any) {
      setMsg(`Download last frame failed (${e?.message || 'error'}). Is FFmpeg installed on the server?`);
    } finally {
      setExtractingFrameJobId(null);
    }
  };

  // NEW: upload a fully custom MP4/WebM and attach trigger phrases to it
  const uploadCustomVideo = async (file: File) => {
    if (!canManage) return;

    if (!uploadsAllowed) {
      setMsg('Confirm upload rights before uploading custom videos.');
      return;
    }

    const label = customVideoLabel.trim();
    const synonyms = customVideoTriggers.trim();

    if (!label) {
      setMsg('Enter a name for the custom video first.');
      return;
    }

    if (!synonyms) {
      setMsg('Enter at least one trigger phrase first.');
      return;
    }

    const normalizeSlug = (raw: string) =>
      raw
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '_')
        .replace(/_+/g, '_')
        .replace(/^_|_$/g, '');

    const baseSlug = normalizeSlug(label) || 'custom_video';

    // Avoid slug collisions in the current local action list.
    let actionSlug = baseSlug;
    let counter = 2;
    while (
      actions.some((a) => (a.actionSlug || '').toLowerCase() === actionSlug.toLowerCase())
    ) {
      actionSlug = `${baseSlug}_${counter}`;
      counter += 1;
    }

    setUploadingId('custom-video');
    setMsg(null);

    try {
      const url = await creatorChatAdminService.uploadActionVideo(file);
      await creatorChatAdminService.createAction(modalId, {
        actionSlug,
        label,
        videoUrl: url,
        synonyms,
        active: true,
        showOnTriggerWheel: customVideoShowOnWheel,
      });

      await load();

      setCustomVideoLabel('');
      setCustomVideoTriggers('');

      setMsg(`Custom video "${label}" uploaded successfully.`);
    } catch (e: any) {
      setMsg(`Custom video upload failed (${e?.message || 'error'})`);
    } finally {
      setUploadingId(null);
    }
  };

  const addTemplate = (t: (typeof ACTION_TEMPLATES)[0]) => {
    if (!uploadsAllowed) {
      setMsg('Confirm you have rights to upload video content before adding actions.');
      return;
    }
    if (actions.some((a) => a.actionSlug === t.slug)) {
      setMsg(`Action "${t.slug}" already exists`);
      return;
    }
    setPendingTemplate(t);
    templateFileRef.current?.click();
  };

  const onTemplateFile = async (file: File) => {
    const t = pendingTemplate;
    setPendingTemplate(null);
    if (!t || !canManage) return;
    setUploadingId(`new-${t.slug}`);
    setMsg(null);
    try {
      const url = await creatorChatAdminService.uploadActionVideo(file);
      await creatorChatAdminService.createAction(modalId, {
        actionSlug: t.slug,
        label: t.label,
        videoUrl: url,
        synonyms: t.synonyms,
        active: true,
      });
      await load();
      setMsg(`Added "${t.label}" with video`);
    } catch (e: any) {
      setMsg(`Add failed (${e?.message || 'error'})`);
    } finally {
      setUploadingId(null);
    }
  };

  const uploadVideoFor = async (action: CreatorChatActionRow, file: File) => {
    if (!uploadsAllowed) {
      setMsg('Confirm upload rights before replacing action videos.');
      return;
    }
    setUploadingId(`action-${action.id}`);
    setMsg(null);
    try {
      const url = await creatorChatAdminService.uploadActionVideo(file);
      await creatorChatAdminService.updateAction(modalId, action.id, { videoUrl: url, active: true });
      await load();
      setMsg(`Video uploaded for ${action.label || action.actionSlug}`);
    } catch (e: any) {
      setMsg(`Upload failed (${e?.message || 'error'})`);
    } finally {
      setUploadingId(null);
    }
  };

  const uploadIdle = async (file: File) => {
    if (!uploadsAllowed) {
      setMsg('Confirm upload rights before uploading idle video.');
      return;
    }
    setUploadingId('idle');
    try {
      const url = await creatorChatAdminService.uploadIdleVideo(file);
      onIdleVideoUrlChange(url);
      setMsg('Idle loop video uploaded — click Save on the modal');
    } catch (e: any) {
      setMsg(`Idle upload failed (${e?.message || 'error'})`);
    } finally {
      setUploadingId(null);
    }
  };

  const saveSynonyms = async (action: CreatorChatActionRow, synonyms: string) => {
    if (!canManage) return;
    try {
      await creatorChatAdminService.updateAction(modalId, action.id, { synonyms });
      await load();
    } catch {
      setMsg('Failed to save trigger phrases');
    }
  };

  const isOnTriggerWheel = (action?: CreatorChatActionRow) =>
    !!action && action.showOnTriggerWheel !== false;

  const isWheelCenter = (action?: CreatorChatActionRow) => !!action && action.triggerWheelCenter === true;

  const setCenterWheel = async (action: CreatorChatActionRow, checked: boolean) => {
    if (!canManage) return;
    setWheelUpdatingId(`center-${action.id}`);
    setMsg(null);
    try {
      await creatorChatAdminService.updateAction(modalId, action.id, { triggerWheelCenter: checked });
      await load();
    } catch {
      setMsg('Failed to update center wheel clip');
    } finally {
      setWheelUpdatingId(null);
    }
  };

  const setWheelVisibility = async (
    checked: boolean,
    opts: { action?: CreatorChatActionRow; job?: CreatorChatVideoJobRow }
  ) => {
    if (!canManage) return;
    const { action, job } = opts;
    const wheelKey = action ? `action-${action.id}` : job ? `job-${job.id}` : 'wheel';
    setWheelUpdatingId(wheelKey);
    setMsg(null);
    try {
      if (action) {
        await creatorChatAdminService.updateAction(modalId, action.id, { showOnTriggerWheel: checked });
      } else if (job) {
        const slug = (job.actionSlug || '').trim();
        const videoUrl = (job.resultVideoUrl || '').trim();
        if (!slug || slug === 'idle' || !videoUrl) return;
        const synonyms = phrasesFromSlug(slug);
        await creatorChatAdminService.createAction(modalId, {
          actionSlug: slug,
          label: synonyms || slug,
          videoUrl,
          synonyms,
          active: true,
          showOnTriggerWheel: checked,
        });
      }
      await load();
    } catch {
      setMsg('Failed to update trigger wheel visibility');
    } finally {
      setWheelUpdatingId(null);
    }
  };

  const findActionForJob = (job: CreatorChatVideoJobRow): CreatorChatActionRow | undefined => {
    const slug = (job.actionSlug || '').trim().toLowerCase();
    if (slug && slug !== 'idle') {
      const bySlug = actions.find((a) => (a.actionSlug || '').toLowerCase() === slug);
      if (bySlug) return bySlug;
    }
    const url = (job.resultVideoUrl || '').trim();
    if (!url) return undefined;
    return actions.find((a) => (a.videoUrl || '') === url);
  };

  const phrasesFromSlug = (slug?: string | null) => (slug || '').replace(/_/g, ' ').trim();

  const saveJobTriggers = async (job: CreatorChatVideoJobRow, synonyms: string) => {
    if (!canManage) return;
    const slug = (job.actionSlug || '').trim();
    if (!slug || slug === 'idle') return;
    const videoUrl = (job.resultVideoUrl || '').trim();
    if (!videoUrl) {
      setMsg('This generation has no video yet — wait until it is Ready.');
      return;
    }
    setMsg(null);
    try {
      const existing = findActionForJob(job);
      if (existing) {
        await creatorChatAdminService.updateAction(modalId, existing.id, { synonyms, active: true });
      } else {
        await creatorChatAdminService.createAction(modalId, {
          actionSlug: slug,
          label: phrasesFromSlug(slug) || slug,
          videoUrl,
          synonyms,
          active: true,
        });
      }
      await load();
      setMsg('Trigger phrases saved.');
    } catch (e: any) {
      setMsg(`Failed to save trigger phrases (${e?.message || 'error'})`);
    }
  };

  const replaceJobVideo = async (job: CreatorChatVideoJobRow, file: File) => {
    if (!uploadsAllowed) {
      setMsg('Confirm upload rights before replacing videos.');
      return;
    }
    const slug = (job.actionSlug || 'custom').trim() || 'custom';
    setUploadingId(`job-${job.id}`);
    setMsg(null);
    try {
      if (slug === 'idle') {
        const url = await creatorChatAdminService.uploadIdleVideo(file);
        onIdleVideoUrlChange(url);
        setMsg('Idle loop video replaced — click Save on the modal.');
      } else {
        const url = await creatorChatAdminService.uploadActionVideo(file);
        const existing = findActionForJob(job);
        if (existing) {
          await creatorChatAdminService.updateAction(modalId, existing.id, { videoUrl: url, active: true });
        } else {
          await creatorChatAdminService.createAction(modalId, {
            actionSlug: slug,
            label: phrasesFromSlug(slug) || slug,
            videoUrl: url,
            synonyms: phrasesFromSlug(slug),
            active: true,
          });
        }
        setMsg(`Video replaced for ${phrasesFromSlug(slug) || slug}.`);
      }
      await load();
    } catch (e: any) {
      setMsg(`Replace video failed (${e?.message || 'error'})`);
    } finally {
      setUploadingId(null);
    }
  };

  const removeJob = async (job: CreatorChatVideoJobRow) => {
    if (!canManage) return;
    const label = job.actionSlug || `#${job.id}`;
    if (!window.confirm(`Delete generated clip "${label}"? This also removes its chat trigger.`)) return;
    setMsg(null);
    // Optimistic UI + invalidate in-flight polls so a stale list cannot bring it back.
    jobsFetchGenRef.current += 1;
    setJobs((prev) => prev.filter((j) => j.id !== job.id));
    setActions((prev) =>
      prev.filter((a) => {
        const slug = (job.actionSlug || '').toLowerCase();
        if (slug && slug !== 'idle' && (a.actionSlug || '').toLowerCase() === slug) return false;
        if (job.resultVideoUrl && a.videoUrl === job.resultVideoUrl) return false;
        return true;
      })
    );
    if ((job.actionSlug || '') === 'idle') {
      onIdleVideoUrlChange('');
    }
    try {
      // Server delete is authoritative: job + action + recoverable FAILED siblings.
      await creatorChatAdminService.deleteVideoJob(modalId, job.id);
      await load();
      setMsg(`Deleted generation ${label}.`);
    } catch (e: any) {
      await load();
      setMsg(`Delete failed (${e?.message || 'error'})`);
    }
  };

  const openLumaModify = (job: CreatorChatVideoJobRow) => {
    setLumaOpenJobId(job.id);
    setLumaPrompt('');
    setLumaMode((videoGenHealth?.luma?.defaultMode as LumaModifyMode) || 'flex_1');
    setLumaTriggers(findActionForJob(job)?.synonyms || phrasesFromSlug(job.actionSlug));
  };

  const modifyWithLuma = async (job: CreatorChatVideoJobRow) => {
    if (!canLumaModify) {
      setMsg(videoGenHealth?.luma?.message || 'Luma is not configured (set creator.chat.luma-video.api-key).');
      return;
    }
    const source = (job.resultVideoUrl || '').trim();
    if (!source) {
      setMsg('This generation has no video yet — wait until it is Ready.');
      return;
    }
    const prompt = lumaPrompt.trim();
    if (!prompt) {
      setMsg('Enter a Luma modify prompt (e.g. "change outfit to red dress").');
      return;
    }
    setModifyingJobId(job.id);
    setMsg(null);
    try {
      await creatorChatAdminService.modifyVideo(modalId, {
        sourceVideoUrl: source,
        prompt,
        mode: lumaMode,
        firstFrameUrl: avatarImageUrl?.trim() || undefined,
        synonyms: lumaTriggers.trim() || undefined,
      });
      setLumaOpenJobId(null);
      setLumaPrompt('');
      await load();
      window.dispatchEvent(new CustomEvent('creator-chat-jobs-changed', { detail: { modalId } }));
      setMsg('Luma modify started — new clip will appear in Recent generations when Ready.');
    } catch (e: any) {
      setMsg(`Luma modify failed (${e?.message || 'error'})`);
    } finally {
      setModifyingJobId(null);
    }
  };

  const removeAction = async (action: CreatorChatActionRow) => {
    if (!canManage || !window.confirm(`Delete action "${action.actionSlug}"?`)) return;
    try {
      await creatorChatAdminService.deleteAction(modalId, action.id);
      await load();
    } catch {
      setMsg('Delete failed');
    }
  };

  const idleJob = latestJobForKind(jobs, 'idle');
  // Manual upload replaces a failed generate — don't keep showing the old fal timeout
  const showIdleJobStatus = idleJob && !(idleVideoUrl && idleJob.status === 'FAILED');
  const idleChip = showIdleJobStatus ? jobStatusChip(idleJob.status) : idleVideoUrl ? jobStatusChip('SUCCEEDED') : null;

  return (
    <div style={{ marginTop: 20, paddingTop: 16, borderTop: '1px solid #e2e8f0' }}>
      <h4 style={{ margin: '0 0 8px', fontSize: 15, color: '#0f172a' }}>Video actions</h4>
      <p style={{ margin: '0 0 12px', fontSize: 12, color: '#64748b' }}>
        Fans can type commands like &quot;blow me a kiss&quot; to trigger clips. Generate from avatar with fal.ai or Sogni, restyle Ready clips with Luma Modify, or upload MP4/WebM manually.
      </p>

      {videoGenHealth && !videoGenHealth.ok && canManage ? (
        <p style={{ fontSize: 12, color: '#b45309', marginBottom: 12, background: '#fffbeb', padding: 8, borderRadius: 8 }}>
          fal.ai video generation unavailable: {videoGenHealth.message || 'Configure creator.chat.video-gen.fal.api-key on the server.'}
        </p>
      ) : null}

      {videoGenHealth && !sogniReady && canManage ? (
        <p style={{ fontSize: 12, color: '#b45309', marginBottom: 12, background: '#fffbeb', padding: 8, borderRadius: 8 }}>
          Sogni unavailable: {videoGenHealth.sogni?.message || 'Set creator.chat.sogni-video.api-key and enabled=true, then restart Java.'}
        </p>
      ) : null}

      {videoGenHealth && !lumaReady && canManage ? (
        <p style={{ fontSize: 12, color: '#b45309', marginBottom: 12, background: '#fffbeb', padding: 8, borderRadius: 8 }}>
          Luma Modify unavailable: {videoGenHealth.luma?.message || 'Set creator.chat.luma-video.api-key and enabled=true, then restart Java.'}
        </p>
      ) : null}

      {sogniReady && canManage ? (
        <p style={{ fontSize: 12, marginBottom: 12, padding: 8, borderRadius: 8, background: '#fdf4ff', color: '#86198f', border: '1px solid #f0abfc' }}>
          Sogni is ON (model {videoGenHealth?.sogni?.model || 'happyhorse'}). Prefer fal Wan for cheap open-source avatar clips — Sogni Unlimited models cannot take avatar image refs via API; only Premium (HappyHorse) works and costs Spark.
        </p>
      ) : null}

      {lumaReady && canManage ? (
        <p style={{ fontSize: 12, marginBottom: 12, padding: 8, borderRadius: 8, background: '#f0f9ff', color: '#0369a1', border: '1px solid #bae6fd' }}>
          Luma Modify is ON — open any Ready clip below and use &quot;Modify with Luma&quot; to restyle it (creates a new action; original kept).
        </p>
      ) : null}

      {videoGenHealth?.ok && canManage ? (
        <p
          style={{
            fontSize: 12,
            marginBottom: 12,
            padding: 8,
            borderRadius: 8,
            background: videoGenHealth.realtimeEnabled ? '#ecfdf5' : '#fff7ed',
            color: videoGenHealth.realtimeEnabled ? '#047857' : '#c2410c',
            border: `1px solid ${videoGenHealth.realtimeEnabled ? '#a7f3d0' : '#fed7aa'}`,
          }}
        >
          {videoGenHealth.realtimeEnabled
            ? 'Realtime chat video-gen is ON — Test persona can create clips when no action video exists (costs fal credits).'
            : 'Realtime chat video-gen is OFF. Set creator.chat.video-gen.realtime.enabled=true and restart Java, or Generate/Upload clips below first.'}
        </p>
      ) : null}

      {msg ? (
        <p style={{ fontSize: 12, color: '#334155', marginBottom: 10, background: '#f1f5f9', padding: 8, borderRadius: 8 }}>{msg}</p>
      ) : null}

      {!uploadsAllowed && canManage ? (
        <p style={{ fontSize: 12, color: '#b45309', marginBottom: 12, background: '#fffbeb', padding: 8, borderRadius: 8 }}>
          Confirm content upload rights above (avatar section) before uploading or generating action videos.
        </p>
      ) : null}

      {!hasAvatar && canManage ? (
        <p style={{ fontSize: 12, color: '#b45309', marginBottom: 12, background: '#fffbeb', padding: 8, borderRadius: 8 }}>
          Upload an avatar image above to enable &quot;Generate from avatar&quot;.
        </p>
      ) : null}

      <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14, fontSize: 13, cursor: canManage ? 'pointer' : 'default' }}>
        <input
          type="checkbox"
          checked={actionsEnabled}
          disabled={!canManage}
          onChange={(e) => onActionsEnabledChange(e.target.checked)}
        />
        <span><strong>Enable video actions</strong> for this modal (requires Save)</span>
      </label>

      {(videoGenReady || sogniReady) && canManage ? (
        <div
          style={{
            marginBottom: 14,
            padding: 10,
            borderRadius: 8,
            border: '1px solid #e2e8f0',
            background: '#f8fafc',
            display: 'flex',
            flexWrap: 'wrap',
            gap: 12,
            alignItems: 'center',
          }}
        >
          <span style={{ fontSize: 12, fontWeight: 700, color: '#334155' }}>Generate provider</span>
          <label
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              fontSize: 12,
              cursor: videoGenReady ? 'pointer' : 'not-allowed',
              opacity: videoGenReady ? 1 : 0.5,
            }}
          >
            <input
              type="radio"
              name="creatorChatGenProvider"
              checked={genProvider === 'fal'}
              disabled={!videoGenReady}
              onChange={() => setGenProvider('fal')}
            />
            fal.ai (Wan open-source)
          </label>
          <label
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              fontSize: 12,
              cursor: sogniReady ? 'pointer' : 'not-allowed',
              opacity: sogniReady ? 1 : 0.5,
            }}
          >
            <input
              type="radio"
              name="creatorChatGenProvider"
              checked={genProvider === 'sogni'}
              disabled={!sogniReady}
              onChange={() => setGenProvider('sogni')}
            />
            Sogni ({videoGenHealth?.sogni?.model || 'happyhorse'})
          </label>
          <span style={{ fontSize: 11, color: '#64748b' }}>
            Active: <strong>{providerLabel}</strong>
            {genProvider === 'fal' && !videoGenReady ? ' (fal unavailable)' : ''}
            {genProvider === 'sogni' && !sogniReady ? ' (Sogni unavailable)' : ''}
          </span>
          {genProvider === 'fal' && videoGenReady ? (
            <label
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                fontSize: 12,
                color: '#334155',
                width: '100%',
                maxWidth: 520,
              }}
            >
              <span style={{ fontWeight: 700, whiteSpace: 'nowrap' }}>fal model</span>
              <select
                value={falModel}
                onChange={(e) => setFalModel(e.target.value)}
                style={{ ...inputStyle, flex: 1, margin: 0 }}
              >
                {falModelOptions.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.label}
                  </option>
                ))}
              </select>
            </label>
          ) : null}
        </div>
      ) : null}

      <div style={{ marginBottom: 14 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 6 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: '#334155' }}>Idle loop video (optional)</div>
          {idleChip ? (
            <span style={{ fontSize: 11, fontWeight: 600, color: idleChip.color, background: idleChip.bg, padding: '2px 8px', borderRadius: 999 }}>
              {idleChip.label}
            </span>
          ) : null}
          {showIdleJobStatus && idleJob?.status === 'FAILED' && idleJob.errorMessage ? (
            <span style={{ fontSize: 11, color: '#b91c1c' }}>{idleJob.errorMessage}</span>
          ) : null}
        </div>
        {idleVideoUrl ? (
          <video src={idleVideoUrl} style={{ width: '100%', maxHeight: 160, borderRadius: 8, background: '#000' }} controls muted loop playsInline />
        ) : null}
        {canManage ? (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 8, alignItems: 'center' }}>
            <button
              type="button"
              className="admin-btn"
              disabled={!canGenerate || generatingKind === 'idle' || !!uploadingId}
              onClick={() => void generateFromAvatar('idle', 'Idle loop')}
            >
              {generatingKind === 'idle' ? 'Starting…' : `Generate idle via ${providerLabel}`}
            </button>
            <input
              type="file"
              accept="video/mp4,video/webm"
              disabled={!uploadsAllowed || uploadingId === 'idle'}
              style={{ fontSize: 12 }}
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) void uploadIdle(f);
                e.target.value = '';
              }}
            />
          </div>
        ) : null}
      </div>

      {canManage ? (
        <>
          <input
            ref={templateFileRef}
            type="file"
            accept="video/mp4,video/webm"
            hidden
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) void onTemplateFile(f);
              e.target.value = '';
            }}
          />
          <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 8, color: '#334155' }}>Action templates</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 14 }}>
            {ACTION_TEMPLATES.map((t) => {
              const job = latestJobForKind(jobs, t.slug);
              const actionHasVideo = actions.some(
                (a) => a.actionSlug === t.slug && a.videoUrl && !a.videoUrl.includes('example.com')
              );
              const showJobStatus = job && !(actionHasVideo && job.status === 'FAILED');
              const chip = showJobStatus
                ? jobStatusChip(job.status)
                : actionHasVideo
                  ? jobStatusChip('SUCCEEDED')
                  : null;
              return (
                <div key={t.slug} style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    <button type="button" className="admin-btn" disabled={!uploadsAllowed || !!uploadingId} onClick={() => addTemplate(t)}>
                      + Upload {t.label}
                    </button>
                    <button
                      type="button"
                      className="admin-btn"
                      disabled={!canGenerate || generatingKind === t.slug || !!uploadingId}
                      onClick={() => void generateFromAvatar(t.slug, t.label)}
                    >
                      {generatingKind === t.slug ? 'Starting…' : `Generate ${t.label} (${providerLabel})`}
                    </button>
                  </div>
                  {chip ? (
                    <span style={{ fontSize: 10, fontWeight: 600, color: chip.color, alignSelf: 'flex-start' }}>
                      {chip.label}
                      {showJobStatus && job?.status === 'FAILED' && job.errorMessage ? ` — ${job.errorMessage}` : ''}
                    </span>
                  ) : null}
                </div>
              );
            })}
          </div>

          <div style={{ marginBottom: 14, padding: 12, border: '1px solid #e2e8f0', borderRadius: 10, background: '#f8fafc' }}>
            <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 6, color: '#334155' }}>Custom request</div>
            <p style={{ margin: '0 0 8px', fontSize: 11, color: '#64748b' }}>
              Type any motion you want. Uses the <strong>Generate provider</strong> selected above (currently{' '}
              {providerLabel}). Multi-stage below has its own fal/Sogni switch.
            </p>
            <textarea
              value={customRequest}
              disabled={!canManage}
              onChange={(e) => setCustomRequest(e.target.value)}
              placeholder="e.g. turn around and smile, or slow fashion swimsuit reveal…"
              rows={2}
              maxLength={300}
              style={{ ...inputStyle, resize: 'vertical', minHeight: 56 }}
            />
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
              <button
                type="button"
                className="admin-btn"
                disabled={!canGenerate || generatingKind === 'custom' || !!uploadingId || !customRequest.trim()}
                onClick={() => void generateFromAvatar('custom', customRequest.trim().slice(0, 40) || 'Custom', customRequest)}
              >
                {generatingKind === 'custom' ? 'Starting…' : `Generate custom via ${providerLabel}`}
              </button>
            <button
              type="button"
              className="admin-btn"
              disabled={!uploadsAllowed || !!uploadingId || !customVideoLabel.trim() || !customVideoTriggers.trim()}
              onClick={() => customVideoFileRef.current?.click()}
            >
              {uploadingId === 'custom-video' ? 'Uploading custom video…' : 'Upload custom video'}
            </button>
              <span style={{ fontSize: 11, color: '#94a3b8' }}>{customRequest.length}/300</span>
            </div>
            <p style={{ margin: '8px 0 0', fontSize: 11, color: '#64748b' }}>
              One motion per generate. Provider above (fal / Sogni) is what will be used — it will not auto-switch.
            </p>
          </div>

          <div
            style={{
              marginBottom: 14,
              padding: 12,
              border: '1px solid #f0abfc',
              borderRadius: 10,
              background: '#fdf4ff',
            }}
          >
            <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 6, color: '#86198f' }}>
              Multi-stage (last-frame chaining)
            </div>
            <p style={{ margin: '0 0 10px', fontSize: 11, color: '#6b21a8', lineHeight: 1.45 }}>
              Stage 1 uses the modal avatar. After a clip is Ready, use <strong>Use last frame → Stage 2/3</strong> on
              that job, then generate the next stage. Pick <strong>fal</strong> for open-source Wan (less vendor NSFW
              blocking) or <strong>Sogni</strong> (HappyHorse Spark; Green net may fail undress). Each stage is a
              separate job — stitch in an editor if you want one file.
            </p>

            <div
              style={{
                marginBottom: 12,
                padding: 8,
                borderRadius: 8,
                border: '1px solid #e9d5ff',
                background: '#fff',
                display: 'flex',
                flexWrap: 'wrap',
                gap: 12,
                alignItems: 'center',
              }}
            >
              <span style={{ fontSize: 12, fontWeight: 700, color: '#6b21a8' }}>Stage provider</span>
              <label
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  fontSize: 12,
                  cursor: videoGenReady ? 'pointer' : 'not-allowed',
                  opacity: videoGenReady ? 1 : 0.5,
                }}
              >
                <input
                  type="radio"
                  name="creatorChatStageProvider"
                  checked={stageProvider === 'fal'}
                  disabled={!videoGenReady}
                  onChange={() => setStageProvider('fal')}
                />
                fal.ai
              </label>
              <label
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  fontSize: 12,
                  cursor: sogniReady ? 'pointer' : 'not-allowed',
                  opacity: sogniReady ? 1 : 0.5,
                }}
              >
                <input
                  type="radio"
                  name="creatorChatStageProvider"
                  checked={stageProvider === 'sogni'}
                  disabled={!sogniReady}
                  onChange={() => setStageProvider('sogni')}
                />
                Sogni
              </label>
              {stageProvider === 'fal' && videoGenReady ? (
                <label
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    fontSize: 12,
                    color: '#334155',
                    flex: '1 1 220px',
                    maxWidth: 480,
                  }}
                >
                  <span style={{ fontWeight: 700, whiteSpace: 'nowrap' }}>fal model</span>
                  <select
                    value={falModel}
                    onChange={(e) => setFalModel(e.target.value)}
                    style={{ ...inputStyle, flex: 1, margin: 0 }}
                  >
                    {falModelOptions.map((m) => (
                      <option key={m.id} value={m.id}>
                        {m.label}
                      </option>
                    ))}
                  </select>
                </label>
              ) : null}
            </div>

            <input
              ref={stage2FrameRef}
              type="file"
              accept="image/*"
              hidden
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) void uploadStageFrame(2, f);
                e.target.value = '';
              }}
            />
            <input
              ref={stage3FrameRef}
              type="file"
              accept="image/*"
              hidden
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) void uploadStageFrame(3, f);
                e.target.value = '';
              }}
            />

            {([1, 2, 3] as const).map((stage) => {
              const prompt =
                stage === 1 ? stage1Prompt : stage === 2 ? stage2Prompt : stage3Prompt;
              const setPrompt =
                stage === 1 ? setStage1Prompt : stage === 2 ? setStage2Prompt : setStage3Prompt;
              const startUrl =
                stage === 1
                  ? avatarImageUrl.trim()
                  : stage === 2
                    ? stage2ImageUrl.trim()
                    : stage3ImageUrl.trim();
              const setStartUrl = stage === 2 ? setStage2ImageUrl : stage === 3 ? setStage3ImageUrl : null;
              const placeholders =
                stage === 1
                  ? 'e.g. soft smile, gentle eye contact, slight head tilt, soft indoor light, photorealistic'
                  : stage === 2
                    ? 'e.g. slow gentle sway, soft smile (or wardrobe change on fal/Vast)'
                    : 'e.g. subtle head turn, warm smile, soft eye contact, photorealistic';
              const stageBtnLabel =
                stageProvider === 'sogni'
                  ? `Generate Stage ${stage} with Sogni`
                  : `Generate Stage ${stage} with fal`;
              return (
                <div
                  key={`multi-stage-${stage}`}
                  style={{
                    marginBottom: stage < 3 ? 12 : 0,
                    padding: 10,
                    borderRadius: 8,
                    border: '1px solid #e9d5ff',
                    background: '#fff',
                  }}
                >
                  <div style={{ fontSize: 11, fontWeight: 700, color: '#6b21a8', marginBottom: 6 }}>
                    Stage {stage}
                    {stage === 1 ? ' — from avatar' : ' — from last frame of previous clip'}
                  </div>
                  <textarea
                    value={prompt}
                    disabled={!canManage}
                    onChange={(e) => setPrompt(e.target.value)}
                    placeholder={placeholders}
                    rows={2}
                    maxLength={500}
                    style={{ ...inputStyle, resize: 'vertical', minHeight: 52 }}
                  />
                  <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 4 }}>{prompt.length}/500</div>

                  {stage > 1 ? (
                    <div style={{ marginTop: 8 }}>
                      <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#475569', marginBottom: 4 }}>
                        Start image (last frame)
                      </label>
                      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                        <button
                          type="button"
                          className="admin-btn"
                          disabled={!uploadsAllowed || !!uploadingId}
                          onClick={() =>
                            stage === 2 ? stage2FrameRef.current?.click() : stage3FrameRef.current?.click()
                          }
                        >
                          {uploadingId === `stage${stage}-frame` ? 'Uploading…' : 'Upload last-frame image'}
                        </button>
                        <input
                          style={{ ...inputStyle, flex: 1, minWidth: 180 }}
                          placeholder="Or paste image URL"
                          value={startUrl}
                          disabled={!canManage}
                          onChange={(e) => setStartUrl?.(e.target.value)}
                        />
                      </div>
                      {startUrl ? (
                        <div style={{ marginTop: 8, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                          <img
                            src={startUrl}
                            alt={`Stage ${stage} start`}
                            style={{
                              width: 72,
                              height: 72,
                              objectFit: 'cover',
                              borderRadius: 8,
                              border: '1px solid #e2e8f0',
                            }}
                          />
                          <button
                            type="button"
                            className="admin-btn"
                            onClick={() =>
                              void downloadImageFromUrl(
                                startUrl,
                                `stage${stage}-start-frame.jpg`
                              ).then((saved) =>
                                setMsg(
                                  saved
                                    ? `Downloaded Stage ${stage} start image.`
                                    : `Opened Stage ${stage} image in a new tab — right-click → Save image as…`
                                )
                              )
                            }
                          >
                            Download start image
                          </button>
                          <a
                            href={startUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ fontSize: 11, color: '#086afb' }}
                          >
                            Open in new tab
                          </a>
                        </div>
                      ) : null}
                    </div>
                  ) : (
                    <p style={{ margin: '6px 0 0', fontSize: 11, color: '#64748b' }}>
                      Uses current modal avatar{avatarImageUrl ? '' : ' (upload an avatar first)'}.
                    </p>
                  )}

                  <button
                    type="button"
                    className="admin-btn"
                    style={{ marginTop: 8 }}
                    disabled={
                      !canGenerateStages ||
                      generatingStage !== null ||
                      !!uploadingId ||
                      !prompt.trim() ||
                      (stage > 1 && !startUrl)
                    }
                    onClick={() => void generateStage(stage)}
                  >
                    {generatingStage === stage ? 'Starting…' : stageBtnLabel}
                  </button>
                </div>
              );
            })}
          </div>

        <div
          style={{
            marginBottom: 14,
            padding: 12,
            border: '1px solid #cbd5e1',
            borderRadius: 10,
            background: '#fff',
          }}
        >
          <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 6, color: '#334155' }}>
            Custom uploaded video
          </div>
          <p style={{ margin: '0 0 10px', fontSize: 11, color: '#64748b' }}>
            Upload any MP4 or WebM and assign the phrases that fans can type to trigger it.
          </p>

          <div style={{ marginBottom: 8 }}>
            <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#475569', marginBottom: 4 }}>
              Video name
            </label>
            <input
              type="text"
              value={customVideoLabel}
              disabled={!!uploadingId}
              onChange={(e) => setCustomVideoLabel(e.target.value)}
              placeholder="e.g. Slow turn"
              maxLength={80}
              style={inputStyle}
            />
          </div>

          <div style={{ marginBottom: 10 }}>
            <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#475569', marginBottom: 4 }}>
              Trigger phrases
            </label>
            <input
              type="text"
              value={customVideoTriggers}
              disabled={!!uploadingId}
              onChange={(e) => setCustomVideoTriggers(e.target.value)}
              placeholder="turn around,slow turn,turn for me"
              maxLength={500}
              style={inputStyle}
            />
            <div style={{ marginTop: 4, fontSize: 10, color: '#94a3b8' }}>
              Separate multiple phrases with commas.
            </div>
          </div>

          <label
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              marginBottom: 10,
              fontSize: 11,
              color: '#475569',
              cursor: uploadsAllowed && !uploadingId ? 'pointer' : 'default',
            }}
          >
            <input
              type="checkbox"
              checked={customVideoShowOnWheel}
              disabled={!uploadsAllowed || !!uploadingId}
              onChange={(e) => setCustomVideoShowOnWheel(e.target.checked)}
            />
            Show on trigger wheel (Test persona dartboard)
          </label>

          <input
            ref={customVideoFileRef}
            type="file"
            accept="video/mp4,video/webm"
            hidden
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) void uploadCustomVideo(f);
              e.target.value = '';
            }}
          />

          <button
            type="button"
            className="admin-btn"
            disabled={!uploadsAllowed || !!uploadingId || !customVideoLabel.trim() || !customVideoTriggers.trim()}
            onClick={() => customVideoFileRef.current?.click()}
          >
            {uploadingId === 'custom-video' ? 'Uploading custom video…' : 'Choose video & upload'}
          </button>
        </div>

          {jobs.length > 0 ? (
            <div style={{ marginBottom: 14, padding: 12, border: '1px solid #e2e8f0', borderRadius: 10, background: '#fff' }}>
              <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 8, color: '#334155' }}>Recent generations</div>
              <p style={{ margin: '0 0 8px', fontSize: 11, color: '#64748b' }}>
                fal / Sogni / Luma finish on their side first; this list auto-refreshes. Edit triggers, replace video,
                Modify with Luma, or delete when Ready. Use <strong>Show on trigger wheel</strong> to control which clips
                appear on the Test persona dartboard. Use <strong>Center wheel (special)</strong> for one clip on the
                center button.
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {jobs.slice(0, 8).map((j) => {
                  const chip = jobStatusChip(j.status);
                  const linked = findActionForJob(j);
                  const isIdle = (j.actionSlug || 'idle') === 'idle';
                  const isReady = j.status === 'SUCCEEDED' && !!j.resultVideoUrl;
                  const triggerDefault = linked?.synonyms || phrasesFromSlug(j.actionSlug);
                  const p = (j.provider || 'fal').toLowerCase();
                  const providerLabel = p === 'luma' ? 'Luma' : p === 'sogni' ? 'Sogni' : 'fal';
                  const providerColor = providerLabel === 'Luma' ? '#0369a1' : providerLabel === 'Sogni' ? '#86198f' : '#7c3aed';
                  const providerBg = providerLabel === 'Luma' ? '#e0f2fe' : providerLabel === 'Sogni' ? '#fae8ff' : '#f3e8ff';
                  const lumaPanelOpen = lumaOpenJobId === j.id;
                  return (
                    <div
                      key={j.id}
                      style={{
                        padding: '8px 10px',
                        background: '#f8fafc',
                        borderRadius: 8,
                        border: '1px solid #e2e8f0',
                      }}
                    >
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'flex-start', justifyContent: 'space-between' }}>
                        <div style={{ minWidth: 0, flex: 1 }}>
                          <div style={{ fontSize: 12, fontWeight: 600, color: '#0f172a' }}>
                            {j.actionSlug || 'idle'}
                            <span style={{ fontWeight: 400, color: '#64748b', marginLeft: 6 }}>#{j.id}</span>
                            <span
                              style={{
                                marginLeft: 6,
                                fontSize: 10,
                                fontWeight: 600,
                                color: providerColor,
                                background: providerBg,
                                padding: '1px 6px',
                                borderRadius: 999,
                              }}
                            >
                              {providerLabel}
                            </span>
                          </div>
                          {j.errorMessage ? (
                            <div style={{ fontSize: 11, color: j.status === 'FAILED' ? '#b91c1c' : '#0369a1', marginTop: 2 }}>
                              {j.errorMessage}
                            </div>
                          ) : null}
                          {isReady ? (
                            <video
                              src={j.resultVideoUrl || undefined}
                              style={{ width: '100%', maxHeight: 120, marginTop: 6, borderRadius: 6, background: '#000' }}
                              controls
                              muted
                              playsInline
                              preload="metadata"
                              onError={(e) => {
                                const el = e.currentTarget;
                                el.style.outline = '1px solid #f87171';
                                el.title = 'Video failed to load — file may be missing or not a real MP4. Delete and regenerate.';
                              }}
                            />
                          ) : null}
                        </div>
                        <span
                          style={{
                            fontSize: 11,
                            fontWeight: 600,
                            color: chip.color,
                            background: chip.bg,
                            padding: '2px 8px',
                            borderRadius: 999,
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {chip.label}
                        </span>
                      </div>
                      {canManage && isReady && !isIdle ? (
                        <div style={{ marginTop: 8 }}>
                          <label style={{ fontSize: 11, fontWeight: 600, color: '#64748b' }}>Trigger phrases (comma-separated)</label>
                          <input
                            key={`${j.id}-${triggerDefault}`}
                            style={{ ...inputStyle, marginTop: 4 }}
                            defaultValue={triggerDefault}
                            disabled={!canManage}
                            placeholder="show me your body, show me your bum"
                            onBlur={(e) => void saveJobTriggers(j, e.target.value)}
                          />
                          <label
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 8,
                              marginTop: 8,
                              fontSize: 11,
                              color: '#475569',
                              cursor: canManage ? 'pointer' : 'default',
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={isOnTriggerWheel(linked)}
                              disabled={!canManage || wheelUpdatingId === `job-${j.id}` || wheelUpdatingId === `action-${linked?.id}`}
                              onChange={(e) => void setWheelVisibility(e.target.checked, { action: linked, job: j })}
                            />
                            Show on trigger wheel
                          </label>
                          {linked && isReady ? (
                            <label
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 8,
                                marginTop: 6,
                                fontSize: 11,
                                color: '#7c3aed',
                                cursor: canManage ? 'pointer' : 'default',
                              }}
                            >
                              <input
                                type="checkbox"
                                checked={isWheelCenter(linked)}
                                disabled={
                                  !canManage ||
                                  wheelUpdatingId === `center-${linked.id}` ||
                                  wheelUpdatingId === `action-${linked?.id}`
                                }
                                onChange={(e) => void setCenterWheel(linked, e.target.checked)}
                              />
                              Center wheel (special clip)
                            </label>
                          ) : null}
                        </div>
                      ) : null}
                      {canManage && isReady ? (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', marginTop: 8 }}>
                          <button
                            type="button"
                            className="admin-btn"
                            disabled={extractingFrameJobId === j.id || !!uploadingId}
                            onClick={() => void applyLastFrameForStage(j, 2)}
                            title="Extract last frame and set as Stage 2 start image"
                          >
                            {extractingFrameJobId === j.id ? 'Extracting…' : 'Use last frame → Stage 2'}
                          </button>
                          <button
                            type="button"
                            className="admin-btn"
                            disabled={extractingFrameJobId === j.id || !!uploadingId}
                            onClick={() => void applyLastFrameForStage(j, 3)}
                            title="Extract last frame and set as Stage 3 start image"
                          >
                            {extractingFrameJobId === j.id ? 'Extracting…' : 'Use last frame → Stage 3'}
                          </button>
                          <button
                            type="button"
                            className="admin-btn"
                            disabled={extractingFrameJobId === j.id || !!uploadingId}
                            onClick={() => void downloadLastFrame(j)}
                            title="Extract last frame JPEG and download it to your computer"
                          >
                            {extractingFrameJobId === j.id ? 'Extracting…' : 'Download last frame'}
                          </button>
                          <input
                            type="file"
                            accept="video/mp4,video/webm"
                            disabled={!uploadsAllowed || uploadingId === `job-${j.id}`}
                            style={{ fontSize: 12 }}
                            onChange={(e) => {
                              const f = e.target.files?.[0];
                              if (f) void replaceJobVideo(j, f);
                              e.target.value = '';
                            }}
                          />
                          {uploadingId === `job-${j.id}` ? (
                            <span style={{ fontSize: 11, color: '#64748b' }}>Uploading…</span>
                          ) : null}
                          <button
                            type="button"
                            className="admin-btn"
                            disabled={!canLumaModify || modifyingJobId === j.id}
                            onClick={() => (lumaPanelOpen ? setLumaOpenJobId(null) : openLumaModify(j))}
                          >
                            {lumaPanelOpen ? 'Hide Luma' : 'Modify with Luma'}
                          </button>
                          <button
                            type="button"
                            className="admin-btn"
                            style={{ color: '#b91c1c' }}
                            onClick={() => void removeJob(j)}
                          >
                            Delete
                          </button>
                        </div>
                      ) : canManage && j.status === 'FAILED' ? (
                        <div style={{ marginTop: 8 }}>
                          <button
                            type="button"
                            className="admin-btn"
                            style={{ color: '#b91c1c' }}
                            onClick={() => void removeJob(j)}
                          >
                            Delete
                          </button>
                        </div>
                      ) : null}
                      {canManage && isReady && lumaPanelOpen ? (
                        <div
                          style={{
                            marginTop: 10,
                            padding: 10,
                            borderRadius: 8,
                            border: '1px solid #bae6fd',
                            background: '#f0f9ff',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 8,
                          }}
                        >
                          <label style={{ fontSize: 11, fontWeight: 600, color: '#0369a1' }}>Luma modify prompt</label>
                          <textarea
                            style={{ ...inputStyle, minHeight: 64, resize: 'vertical' }}
                            value={lumaPrompt}
                            onChange={(e) => setLumaPrompt(e.target.value)}
                            placeholder="e.g. change outfit to a red dress, cinematic lighting"
                            maxLength={500}
                          />
                          <label style={{ fontSize: 11, fontWeight: 600, color: '#0369a1' }}>Mode</label>
                          <select
                            style={inputStyle}
                            value={lumaMode}
                            onChange={(e) => setLumaMode(e.target.value as LumaModifyMode)}
                          >
                            {LUMA_MODES.map((m) => (
                              <option key={m.value} value={m.value}>
                                {m.label}
                              </option>
                            ))}
                          </select>
                          <label style={{ fontSize: 11, fontWeight: 600, color: '#0369a1' }}>
                            Trigger phrases for the new clip (optional)
                          </label>
                          <input
                            style={inputStyle}
                            value={lumaTriggers}
                            onChange={(e) => setLumaTriggers(e.target.value)}
                            placeholder="red dress, show red outfit"
                          />
                          <button
                            type="button"
                            className="admin-btn"
                            disabled={!canLumaModify || modifyingJobId === j.id || !lumaPrompt.trim()}
                            onClick={() => void modifyWithLuma(j)}
                          >
                            {modifyingJobId === j.id ? 'Submitting to Luma…' : 'Start Luma modify'}
                          </button>
                        </div>
                      ) : null}
                    </div>
                  );
                })}
              </div>
            </div>
          ) : null}

          {needsJobPolling ? (
            <p style={{ fontSize: 11, color: '#64748b', marginBottom: 12 }}>
              Video generation / import in progress — status refreshes every 5s. When Ready, click <strong>Save</strong>, enable video actions, then Test persona.
            </p>
          ) : null}
        </>
      ) : null}

      {loading ? <p style={{ fontSize: 12, color: '#94a3b8' }}>Loading actions…</p> : null}

      {actions.map((a) => (
        <div
          key={a.id}
          style={{
            border: '1px solid #e2e8f0',
            borderRadius: 10,
            padding: 12,
            marginBottom: 10,
            background: a.active ? '#fff' : '#f8fafc',
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
            <div>
              <strong style={{ fontSize: 13 }}>{a.label || a.actionSlug}</strong>
              <span style={{ fontSize: 11, color: '#64748b', marginLeft: 8 }}>({a.actionSlug})</span>
              {!a.active ? <span style={{ fontSize: 11, color: '#b45309', marginLeft: 8 }}>inactive</span> : null}
            </div>
            {canManage ? (
              <button type="button" className="admin-btn" style={{ color: '#b91c1c' }} onClick={() => void removeAction(a)}>
                Delete
              </button>
            ) : null}
          </div>
          {a.videoUrl && !a.videoUrl.includes('example.com') ? (
            <video
              src={a.videoUrl}
              style={{ width: '100%', maxHeight: 140, marginTop: 8, borderRadius: 8, background: '#000' }}
              controls
              muted
              playsInline
            />
          ) : (
            <p style={{ fontSize: 11, color: '#94a3b8', marginTop: 8 }}>No video uploaded yet</p>
          )}
          <div style={{ marginTop: 8 }}>
            <label style={{ fontSize: 11, fontWeight: 600, color: '#64748b' }}>Trigger phrases (comma-separated)</label>
            <input
              style={{ ...inputStyle, marginTop: 4 }}
              defaultValue={a.synonyms || ''}
              disabled={!canManage}
              onBlur={(e) => void saveSynonyms(a, e.target.value)}
            />
            {canManage ? (
              <label
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  marginTop: 8,
                  fontSize: 11,
                  color: '#475569',
                  cursor: 'pointer',
                }}
              >
                <input
                  type="checkbox"
                  checked={isOnTriggerWheel(a)}
                  disabled={wheelUpdatingId === `action-${a.id}`}
                  onChange={(e) => void setWheelVisibility(e.target.checked, { action: a })}
                />
                Show on trigger wheel
              </label>
            ) : null}
            {canManage && a.videoUrl && !a.videoUrl.includes('example.com') ? (
              <label
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  marginTop: 6,
                  fontSize: 11,
                  color: '#7c3aed',
                  cursor: 'pointer',
                }}
              >
                <input
                  type="checkbox"
                  checked={isWheelCenter(a)}
                  disabled={wheelUpdatingId === `center-${a.id}`}
                  onChange={(e) => void setCenterWheel(a, e.target.checked)}
                />
                Center wheel (special clip)
              </label>
            ) : null}
          </div>
          {canManage ? (
            <input
              type="file"
              accept="video/mp4,video/webm"
              disabled={!uploadsAllowed || uploadingId === `action-${a.id}`}
              style={{ marginTop: 8, fontSize: 12 }}
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) void uploadVideoFor(a, f);
                e.target.value = '';
              }}
            />
          ) : null}
        </div>
      ))}
    </div>
  );
};

export default AdminCreatorChatActionsPanel;
